import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { FRONTEND_URL } from "../config/axios";
import logo from "../assets/images/logo.png";
import "./NavbarStyles.css";

// Helper SVG Icons
const DropdownArrow = ({ color = "#5a6874", isOpen = false }) => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ transition: "transform 0.2s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
    >
        <path
            d="M6 9L12 15L18 9"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const MenuIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2">
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const Navbar = ({ sidebarData, userName, userRole, userEmail }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const dropdownRefs = useRef({});
    const profileRef = useRef(null);

    // Get current path - this is your actual URL
    const currentPath = location.pathname;

    // Extract the last part of the URL for matching
    const getLastPathSegment = (path) => {
        const parts = path.split('/').filter(Boolean);
        return parts.length > 0 ? parts[parts.length - 1] : '';
    };

    // FIXED: Smart path matching that works with your actual URLs
    const isActive = (linkPath) => {
        if (!linkPath) return false;

        // Remove leading slash for comparison
        let cleanCurrent = currentPath.replace(/^\/+/, '');
        let cleanLink = linkPath.replace(/^\/+/, '');

        // Case 1: Exact match
        if (cleanCurrent === cleanLink) return true;

        // Case 2: Current path ends with the link path (for nested routes)
        if (cleanCurrent.endsWith('/' + cleanLink)) return true;
        if (cleanCurrent.endsWith(cleanLink)) return true;

        // Case 3: Link path is the last segment of current path
        const lastSegment = getLastPathSegment(cleanCurrent);
        if (lastSegment === cleanLink) return true;

        // Case 4: Current path contains the link path as a segment
        const currentSegments = cleanCurrent.split('/');
        if (currentSegments.includes(cleanLink)) return true;

        return false;
    };

    // Check if any submenu item is active
    const isSubmenuActive = (subItems) => {
        if (!subItems || subItems.length === 0) return false;
        return subItems.some(item => {
            const itemPath = item.userId || item.path || "";
            return isActive(itemPath);
        });
    };

    // Get the active submenu item
    const getActiveSubmenuItem = (subItems) => {
        if (!subItems) return null;
        return subItems.find(item => {
            const itemPath = item.userId || item.path || "";
            return isActive(itemPath);
        });
    };

    // Debug logging - shows what's being compared
    useEffect(() => {

        if (sidebarData && sidebarData.length > 0) {
            sidebarData.forEach(item => {
                const itemPath = item.userId || item.path || "";
                const isItemActive = isActive(itemPath);
                console.log(`📁 ${item.name}: path="${itemPath}" -> ACTIVE: ${isItemActive}`);

                if (item.subMenu) {
                    item.subMenu.forEach(sub => {
                        const subPath = sub.userId || sub.path || "";
                        const isSubActive = isActive(subPath);
                        console.log(`   └─ ${sub.name}: path="${subPath}" -> ACTIVE: ${isSubActive}`);
                        if (isSubActive) {
                            console.log(`      ✅ ACTIVE PAGE: ${sub.name}`);
                        }
                    });
                }
            });
        }
    }, [currentPath, sidebarData]);

    // Get user info from sessionStorage
    const empname = sessionStorage.getItem("name") || sessionStorage.getItem("userName") || userName || "User";
    const empCode = sessionStorage.getItem("sitemanagement_emp_code");
    const role = sessionStorage.getItem("sitemanagement_role") || sessionStorage.getItem("role") || userRole || "User";
    const EMAIL = sessionStorage.getItem("email") || sessionStorage.getItem("userEmail") || userEmail || "";

    // Get first letter for avatar
    const getFirstLetter = () => {
        if (empname && empname !== "User") {
            return empname.charAt(0).toUpperCase();
        } else if (empCode) {
            return empCode.charAt(0).toUpperCase();
        }
        return "U";
    };

    // Handle logout
    const handleLogout = () => {
        toast.info("Logging Out...");
        sessionStorage.clear();
        setTimeout(() => {
            window.location.href = FRONTEND_URL || "/";
        }, 500);
    };

    // Handle dropdown toggle
    const toggleDropdown = (dropdownId) => {
        setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown !== null) {
                const dropdownElement = dropdownRefs.current[openDropdown];
                if (dropdownElement && !dropdownElement.contains(event.target)) {
                    setOpenDropdown(null);
                }
            }
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [openDropdown]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1024);
            if (window.innerWidth > 1024) {
                setIsMobileDrawerOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Close mobile drawer on route change
    useEffect(() => {
        setIsMobileDrawerOpen(false);
    }, [location]);

    // Get path for item
    const getItemPath = (item) => {
        return item.userId || item.path || "";
    };

    // Render navigation item
    const renderNavItem = (item, index) => {
        const itemPath = getItemPath(item);

        if (item.subMenu && item.subMenu.length > 0) {
            const isItemActive = isSubmenuActive(item.subMenu);
            const activeSubItem = getActiveSubmenuItem(item.subMenu);

            return (
                <div
                    key={index}
                    className="nav-dropdown"
                    ref={el => dropdownRefs.current[item.name] = el}
                >
                    <button
                        className={`nav-dropdown-trigger ${isItemActive ? "active" : ""}`}
                        onClick={() => toggleDropdown(item.name)}
                    >
                        <div
                            dangerouslySetInnerHTML={{
                                __html: (isItemActive && activeSubItem) ? (activeSubItem.svgfill || activeSubItem.svg) : (item.svgfill || item.svg)
                            }}
                            style={{ display: "flex", alignItems: "center" }}
                        />
                        <span>{item.name}</span>
                        <span className={`dropdown-arrow ${openDropdown === item.name ? "open" : ""}`}>
                            <DropdownArrow color={isItemActive ? "#2563eb" : "#475569"} isOpen={openDropdown === item.name} />
                        </span>
                    </button>
                    {openDropdown === item.name && (
                        <div className="nav-dropdown-menu">
                            <div className="dropdown-header">{item.name}</div>
                            {item.subMenu.map((subItem, subIndex) => {
                                const subPath = getItemPath(subItem);
                                const isSubActive = isActive(subPath);
                                return (
                                    <Link
                                        key={subIndex}
                                        to={subPath}
                                        className={`dropdown-item ${isSubActive ? "active" : ""}`}
                                        onClick={() => setOpenDropdown(null)}
                                    >
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: (subItem.svgfill || subItem.svg)
                                            }}
                                        />
                                        <span>{subItem.name}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        } else {
            const isItemActive = isActive(itemPath);
            return (
                <Link
                    key={index}
                    to={itemPath}
                    className={`nav-link-item ${isItemActive ? "active" : ""}`}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: (item.svgfill || item.svg)
                        }}
                        style={{ display: "flex", alignItems: "center" }}
                    />
                    <span>{item.name}</span>
                </Link>
            );
        }
    };

    // Mobile drawer item
    const MobileDrawerItem = ({ item, index }) => {
        const [isDrawerOpen, setIsDrawerOpen] = useState(false);
        const itemPath = getItemPath(item);

        useEffect(() => {
            if (item.subMenu && isSubmenuActive(item.subMenu)) {
                setIsDrawerOpen(true);
            }
        }, [currentPath]);

        if (item.subMenu && item.subMenu.length > 0) {
            const isItemActive = isSubmenuActive(item.subMenu);
            const activeSubItem = getActiveSubmenuItem(item.subMenu);

            return (
                <div key={index}>
                    <button
                        className={`drawer-nav-item ${isItemActive ? "active" : ""}`}
                        onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                        style={{
                            width: "100%",
                            justifyContent: "space-between",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer"
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: (isItemActive && activeSubItem) ? (activeSubItem.svgfill || activeSubItem.svg) : (item.svgfill || item.svg)
                                }}
                            />
                            <span>{item.name}</span>
                        </div>
                        <DropdownArrow
                            color={isItemActive ? "#2563eb" : "#475569"}
                            isOpen={isDrawerOpen}
                        />
                    </button>
                    {isDrawerOpen && (
                        <div style={{ marginLeft: "16px" }}>
                            {item.subMenu.map((subItem, subIndex) => {
                                const subPath = getItemPath(subItem);
                                const isSubActive = isActive(subPath);
                                return (
                                    <Link
                                        key={subIndex}
                                        to={subPath}
                                        className={`drawer-dropdown-item ${isSubActive ? "active" : ""}`}
                                        onClick={() => setIsMobileDrawerOpen(false)}
                                    >
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: (subItem.svgfill || subItem.svg)
                                            }}
                                        />
                                        <span>{subItem.name}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        const isItemActive = isActive(itemPath);
        return (
            <Link
                key={index}
                to={itemPath}
                className={`drawer-nav-item ${isItemActive ? "active" : ""}`}
                onClick={() => setIsMobileDrawerOpen(false)}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: (item.svgfill || item.svg)
                        }}
                    />
                    <span>{item.name}</span>
                </div>
            </Link>
        );
    };

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="navbar-container">
                <div className="navbar-inner">
                    {/* Logo Section */}
                    <div className="navbar-logo-section">
                        <img
                            src={logo}
                            alt="Logo"
                            className="navbar-logo"
                            onClick={() => navigate("/")}
                            style={{ cursor: "pointer" }}
                        />
                    </div>

                    {/* Desktop Navigation Links */}
                    {!isMobile && (
                        <div className="navbar-links-section">
                            {sidebarData && sidebarData.map((item, index) => renderNavItem(item, index))}
                        </div>
                    )}

                    {/* Mobile Menu Toggle */}
                    {isMobile && (
                        <button
                            className="mobile-menu-toggle"
                            onClick={() => setIsMobileDrawerOpen(true)}
                        >
                            <MenuIcon />
                        </button>
                    )}

                    {/* Right Section - Profile & Logout */}
                    <div className="navbar-right-section">
                        <div className="profile-dropdown-container" ref={profileRef}>
                            <button
                                className="profile-button"
                                onClick={() => setShowProfileMenu(!showProfileMenu)}
                            >
                                <div className="profile-avatar">{getFirstLetter()}</div>
                                {!isMobile && <span className="profile-name">{empname !== "User" ? empname : empCode || "User"}</span>}
                            </button>

                            {showProfileMenu && (
                                <div className="profile-menu">
                                    <div className="profile-info">
                                        <div className="profile-name">{empname !== "User" ? empname : empCode || "User"}</div>
                                        <div className="profile-role">{role}</div>
                                        {EMAIL && <div className="profile-email">{EMAIL}</div>}
                                    </div>
                                    <div className="profile-divider"></div>
                                    <button className="logout-btn" onClick={handleLogout}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                            <polyline points="16 17 21 12 16 7" />
                                            <line x1="21" y1="12" x2="9" y2="12" />
                                        </svg>
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Drawer */}
            {isMobile && (
                <>
                    <div className={`mobile-drawer ${isMobileDrawerOpen ? "open" : ""}`}>
                        <div className="drawer-header">
                            <img src={logo} alt="Logo" className="drawer-logo" />
                            <button className="close-drawer" onClick={() => setIsMobileDrawerOpen(false)}>
                                <CloseIcon />
                            </button>
                        </div>
                        <div className="drawer-nav-links">
                            {sidebarData && sidebarData.map((item, index) => (
                                <MobileDrawerItem key={index} item={item} index={index} />
                            ))}
                        </div>
                        <div className="drawer-footer">
                            <button className="drawer-logout" onClick={handleLogout}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <polyline points="16 17 21 12 16 7" />
                                    <line x1="21" y1="12" x2="9" y2="12" />
                                </svg>
                                Logout
                            </button>
                        </div>
                    </div>
                    {isMobileDrawerOpen && (
                        <div className="drawer-overlay" onClick={() => setIsMobileDrawerOpen(false)} />
                    )}
                </>
            )}
        </>
    );
};

export default Navbar;